import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import store from '../data/store';
import Config from '../config';
import { logout, setAstroToken } from '../data/stores/authStore';
// import { logout } from './actions/authActions';

const defaultConfig: AxiosRequestConfig = {
	baseURL: Config.apiBaseUrl,
	timeout: 90000,
};

let refreshPromise: Promise<string> | null = null;

const REFRESH_BROADCAST_CHANNEL = 'or_auth_refresh';
const REFRESH_LOCK_KEY = 'or_auth_refresh_lock';
const REFRESH_LOCK_TTL_MS = 15000;

type RefreshLock = {
	ownerId: string;
	requestId: string;
	expiresAt: number;
};

type RefreshBroadcastMessage =
	| {
			type: 'OR_REFRESH_RESULT';
			requestId: string;
			ok: true;
			accessToken: string;
			ownerId: string;
	  }
	| {
			type: 'OR_REFRESH_RESULT';
			requestId: string;
			ok: false;
			error: any;
			ownerId: string;
	  };

function getTabId(): string {
	try {
		const existing = window.sessionStorage.getItem('or_tab_id');
		if (existing) return existing;
		const id =
			// @ts-ignore
			(window.crypto?.randomUUID?.() as string | undefined) ??
			`${Date.now()}_${Math.random().toString(16).slice(2)}`;
		window.sessionStorage.setItem('or_tab_id', id);
		return id;
	} catch {
		return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
	}
}

function safeParseJson<T>(value: string | null): T | null {
	if (!value) return null;
	try {
		return JSON.parse(value) as T;
	} catch {
		return null;
	}
}

function readRefreshLock(): RefreshLock | null {
	return safeParseJson<RefreshLock>(
		window.localStorage.getItem(REFRESH_LOCK_KEY)
	);
}

function writeRefreshLock(lock: RefreshLock): void {
	window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
}

function clearRefreshLockIfOwned(ownerId: string): void {
	const current = readRefreshLock();
	if (current?.ownerId === ownerId) {
		window.localStorage.removeItem(REFRESH_LOCK_KEY);
	}
}

function tryAcquireRefreshLock(
	ownerId: string,
	requestId: string
): RefreshLock | null {
	const now = Date.now();
	const existing = readRefreshLock();
	if (existing && existing.expiresAt > now && existing.ownerId !== ownerId) {
		return null;
	}

	const lock: RefreshLock = {
		ownerId,
		requestId,
		expiresAt: now + REFRESH_LOCK_TTL_MS,
	};
	writeRefreshLock(lock);

	// Verify we actually own it (last write wins).
	const confirmed = readRefreshLock();
	if (confirmed?.ownerId === ownerId && confirmed?.requestId === requestId) {
		return lock;
	}
	return null;
}

function createBroadcastChannel(): BroadcastChannel | null {
	try {
		if (typeof BroadcastChannel === 'undefined') return null;
		return new BroadcastChannel(REFRESH_BROADCAST_CHANNEL);
	} catch {
		return null;
	}
}

type RefreshErrorPayload = {
	status?: number;
	statusCode?: number;
	code?: unknown;
	message?: string;
	data?: any;
};

function normalizeRefreshError(error: unknown): RefreshErrorPayload {
	const axiosError = error as AxiosError<any>;
	const status = axiosError?.response?.status;
	const data = axiosError?.response?.data;

	// Keep it structured-cloneable for BroadcastChannel and preserve status info.
	return {
		status,
		statusCode: status,
		code:
			data?.error?.code ??
			data?.code ??
			(axiosError as any)?.code ??
			(undefined as unknown),
		message:
			data?.message ??
			(data?.error?.message as string | undefined) ??
			axiosError?.message ??
			(typeof error === 'string' ? error : undefined),
		data,
	};
}

async function doNetworkRefresh(): Promise<string> {
	return axios({
		baseURL: Config.apiBaseUrl,
		url: '/auth/refresh',
		method: 'POST',
		withCredentials: true,
		headers: {
			'X-Client-Type': 'web',
		},
	})
		.then((r) => {
			const accessToken = r.data?.accessToken;
			if (
				typeof accessToken !== 'string' ||
				accessToken.trim().length === 0
			) {
				// Ensure callers never receive an undefined/empty token.
				throw normalizeRefreshError({
					message: 'Malformed refresh response: missing accessToken',
					data: r.data,
				});
			}
			return accessToken;
		})
		.catch((error: unknown) => {
			throw normalizeRefreshError(error);
		});
}

function waitForRefreshResult(
	channel: BroadcastChannel,
	requestId: string,
	timeoutMs: number
): Promise<string> {
	return new Promise((resolve, reject) => {
		let settled = false;
		const timeout = window.setTimeout(() => {
			if (settled) return;
			settled = true;
			channel.removeEventListener('message', onMessage as any);
			reject(new Error('Timed out waiting for refresh result'));
		}, timeoutMs);

		const onMessage = (ev: MessageEvent) => {
			const msg = ev.data as RefreshBroadcastMessage;
			if (!msg || msg.type !== 'OR_REFRESH_RESULT') return;
			if (msg.requestId !== requestId) return;
			if (settled) return;
			settled = true;
			window.clearTimeout(timeout);
			channel.removeEventListener('message', onMessage as any);
			if (msg.ok) resolve(msg.accessToken);
			else reject(msg.error);
		};

		channel.addEventListener('message', onMessage as any);
	});
}

async function refreshAccessTokenCrossTab(): Promise<string> {
	// If BroadcastChannel isn't available, fall back to per-tab refresh behavior.
	const channel = createBroadcastChannel();
	if (!channel) {
		return doNetworkRefresh();
	}

	const tabId = getTabId();
	const requestId =
		// @ts-ignore
		(window.crypto?.randomUUID?.() as string | undefined) ??
		`${Date.now()}_${Math.random().toString(16).slice(2)}`;

	let lock = tryAcquireRefreshLock(tabId, requestId);

	try {
		// If we can't acquire the lock, wait for the current leader's result.
		if (!lock) {
			let existing = readRefreshLock();

			// If no lock exists, try once more to become leader.
			if (!existing || existing.expiresAt <= Date.now()) {
				lock = tryAcquireRefreshLock(tabId, requestId);
				// Another tab may have acquired the lock between our initial read and
				// this retry. Re-read so we can wait on the current leader's requestId.
				if (!lock) existing = readRefreshLock();
			}

			// Still not leader: wait for the leader tab's refresh result.
			if (!lock) {
				// Use the most up-to-date lock state in case leadership changed.
				const waitForRequestId =
					readRefreshLock()?.requestId ?? existing?.requestId;
				if (!waitForRequestId) {
					// No known leader to wait for; fall back to per-tab refresh.
					return await doNetworkRefresh();
				}

				try {
					return await waitForRefreshResult(
						channel,
						waitForRequestId,
						REFRESH_LOCK_TTL_MS
					);
				} catch (e) {
					// If the lock expired (leader crashed), try to become leader once.
					const stale = readRefreshLock();
					if (!stale || stale.expiresAt <= Date.now()) {
						lock = tryAcquireRefreshLock(tabId, requestId);
						if (!lock) throw e;
					} else {
						throw e;
					}
				}
			}
		}

		// Leader: perform refresh once, broadcast result to all tabs, then release lock.
		try {
			const accessToken = await doNetworkRefresh();
			channel.postMessage({
				type: 'OR_REFRESH_RESULT',
				requestId,
				ok: true,
				accessToken,
				ownerId: tabId,
			} satisfies RefreshBroadcastMessage);
			return accessToken;
		} catch (error: any) {
			channel.postMessage({
				type: 'OR_REFRESH_RESULT',
				requestId,
				ok: false,
				error,
				ownerId: tabId,
			} satisfies RefreshBroadcastMessage);
			throw error;
		} finally {
			clearRefreshLockIfOwned(tabId);
		}
	} finally {
		try {
			channel.close();
		} catch {
			// ignore
		}
	}
}

function isAuthCode(code: unknown): boolean {
	return (
		code === 'TOKEN_EXPIRED' ||
		code === 'AUTH_REQUIRED' ||
		code === 'UNAUTHORIZED'
	);
}

function isGraphqlAuthError(responseData: any): boolean {
	const errors = responseData?.errors;
	if (!Array.isArray(errors) || errors.length === 0) return false;

	return errors.some((err: any) => {
		const message = String(err?.message || '');
		const extensions = err?.extensions || {};

		const statusCode =
			extensions?.response?.statusCode ??
			extensions?.exception?.status ??
			extensions?.originalError?.statusCode;
		if (statusCode === 401) return true;

		// Sometimes codes are embedded in messages.
		if (
			message.includes('TOKEN_EXPIRED') ||
			message.includes('AUTH_REQUIRED') ||
			message.includes('UNAUTHORIZED')
		) {
			return true;
		}

		// Nest HttpException payload can appear under extensions.*
		const possibleCode =
			err?.response?.error?.code ??
			extensions?.response?.error?.code ??
			extensions?.exception?.response?.error?.code ??
			extensions?.originalError?.response?.error?.code;
		if (isAuthCode(possibleCode)) return true;

		return false;
	});
}

function isUnauthorizedRefreshError(error: any): boolean {
	const status =
		error?.response?.status ??
		error?.response?.statusCode ??
		error?.data?.status ??
		error?.data?.statusCode ??
		error?.status ??
		error?.statusCode;
	if (status === 401) return true;

	const code =
		error?.response?.data?.error?.code ??
		error?.data?.error?.code ??
		error?.error?.code ??
		error?.response?.data?.code ??
		error?.data?.code ??
		error?.code;
	if (isAuthCode(code) || code === 'INVALID_REFRESH') return true;

	const msg = String(
		error?.response?.data?.message ??
			error?.data?.message ??
			error?.message ??
			''
	).toLowerCase();
	return msg.includes('unauthorized') || msg.includes('not authenticated');
}

export async function refreshAccessToken(): Promise<string> {
	if (!refreshPromise) {
		refreshPromise = refreshAccessTokenCrossTab().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
}

export default async function axiosClient(
	config: AxiosRequestConfig = defaultConfig
): Promise<AxiosResponse> {
	const internalConfig: any = { ...config };
	const alreadyRetried = Boolean(internalConfig.__orAuthRetried);

	const makeRequest = async (
		overrideToken?: string
	): Promise<AxiosResponse> => {
		const storeState: any = store?.getState?.();
		const token = overrideToken ?? storeState?.authState?.astroToken ?? '';

		const headers: Record<string, any> = {
			...(internalConfig.headers || {}),
		};
		// If we are retrying after a refresh, the refreshed token must win even if
		// the caller supplied an authorization header in the original config.
		if (overrideToken && token) {
			headers.authorization = `Bearer ${token}`;
		} else if (!headers.authorization && token) {
			headers.authorization = `Bearer ${token}`;
		}

		// Avoid leaking internal flags to axios.
		const { __orAuthRetried, ...axiosConfig } = internalConfig;
		return axios({
			...defaultConfig,
			...axiosConfig,
			headers,
		});
	};

	const handleGraphqlErrorsOrThrow = (responseData: any) => {
		const errors = responseData?.errors || [];
		if (Array.isArray(errors) && errors.length) {
			throw errors[0].message || 'An error occurred';
		}
	};

	try {
		const response = await makeRequest();
		const responseData = response.data;

		// GraphQL often returns 200 with errors[].
		if (isGraphqlAuthError(responseData) && !alreadyRetried) {
			try {
				const newToken = await refreshAccessToken();
				if (
					typeof newToken !== 'string' ||
					newToken.trim().length === 0
				) {
					throw new Error('Refresh returned an empty access token');
				}
				store.dispatch(setAstroToken(newToken));
				internalConfig.__orAuthRetried = true;
				const retryResponse = await makeRequest(newToken);
				handleGraphqlErrorsOrThrow(retryResponse.data);
				return retryResponse.data ?? retryResponse;
			} catch (refreshError: any) {
				if (isUnauthorizedRefreshError(refreshError)) {
					store.dispatch(logout());
				}
				throw refreshError?.message || refreshError;
			}
		}

		handleGraphqlErrorsOrThrow(responseData);
		return responseData ?? response;
	} catch (error: any) {
		const axiosError = error as AxiosError<any>;
		const status = axiosError.response?.status;

		// If we received an HTTP 401, we can try a refresh once and then retry.
		if (status === 401 && !alreadyRetried) {
			try {
				const newToken = await refreshAccessToken();
				if (
					typeof newToken !== 'string' ||
					newToken.trim().length === 0
				) {
					throw new Error('Refresh returned an empty access token');
				}
				store.dispatch(setAstroToken(newToken));
				internalConfig.__orAuthRetried = true;
				const retryResponse = await makeRequest(newToken);
				handleGraphqlErrorsOrThrow(retryResponse.data);
				return retryResponse.data ?? retryResponse;
			} catch (refreshError: any) {
				if (isUnauthorizedRefreshError(refreshError)) {
					store.dispatch(logout());
				}
				throw refreshError?.message || refreshError;
			}
		}

		if (status === 403) {
			// Preserve old behavior: some backends used 403 on expired token.
			store.dispatch(logout());
		}
		throw (
			axiosError.response?.data?.message ||
			axiosError.message ||
			axiosError.response?.data ||
			error
		);
	}
}

export async function baseAxiosClient(
	config: AxiosRequestConfig = defaultConfig
): Promise<any> {
	try {
		const response: AxiosResponse = await axios({
			...config,
		});

		if (!!response.data) {
			return response.data;
		}

		return response;
	} catch (error: any) {
		console.error(error);
		throw error.response?.data?.message || error.message || error;
	}
}
